import { useState } from 'react'
import { toast } from 'sonner'
import { AdPlatform } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { R } from '@/modules/api/actions'
import { ManualCreativeUploadForm } from './ManualCreativeUploadForm'
import { useManualCreativeUploadForm } from './useManualCreativeUploadForm'

export type { ManualCreativePayload } from './useManualCreativeUploadForm'

interface ManualCreativeUploadModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Creates a manual AI-create session and reports its id. */
    onSubmit: (sessionId: string) => Promise<void>
    variant?: 'paid' | 'email'
    platform?: AdPlatform
}

export function ManualCreativeUploadModal({
    open,
    ...contentProps
}: ManualCreativeUploadModalProps) {
    return (
        <Dialog open={open} onOpenChange={contentProps.onOpenChange}>
            {open && <ManualCreativeUploadModalContent {...contentProps} />}
        </Dialog>
    )
}

function ManualCreativeUploadModalContent({
    onOpenChange,
    onSubmit,
    variant = 'paid',
    platform,
}: Omit<ManualCreativeUploadModalProps, 'open'>) {
    const isEmail = variant === 'email'
    const creativeTerm = 'storefront'

    const form = useManualCreativeUploadForm(variant)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async () => {
        setIsSubmitting(true)
        try {
            const payload = await form.uploadAndBuild()
            const { sessionId } = await R.createAICreateSessionManual(
                payload.assets,
                payload.adCopy,
                payload.productUrl || undefined,
                payload.creativeName,
                platform,
            )
            await onSubmit(sessionId)
        } catch (error) {
            console.error('Failed to upload creative:', error)
            const message = error instanceof Error ? error.message : undefined
            toast.error(
                message ||
                    'Failed to upload creative. Please try again and if the problem persists, contact support.',
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        if (isSubmitting) return
        onOpenChange(false)
    }

    const preventDismissWhileSubmitting = (event: Event) => {
        if (isSubmitting) event.preventDefault()
    }

    return (
        <DialogContent
            className="max-h-[90vh] max-w-2xl overflow-y-auto"
            onEscapeKeyDown={preventDismissWhileSubmitting}
            onInteractOutside={preventDismissWhileSubmitting}
        >
            <DialogHeader>
                <DialogTitle>Create Storefront from Scratch</DialogTitle>
                <DialogDescription>
                    Provide at least one of: creative file,{' '}
                    {isEmail ? 'message copy' : 'ad copy'}, or product URL to
                    generate a {creativeTerm}
                </DialogDescription>
            </DialogHeader>

            <ManualCreativeUploadForm
                form={form}
                variant={variant}
                disabled={isSubmitting}
            />

            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !form.canSubmit}
                    className="bg-[#276749] text-white hover:opacity-90"
                >
                    {isSubmitting ? 'Uploading...' : 'Optimize Post Click'}
                </Button>
            </div>
        </DialogContent>
    )
}
